import React from 'react';

import { useTaskStatusQuery } from '../../hooks/useTaskStatusQuery';
import type { ProcessingTask } from '../../stores/taskProcessing.types';
import {
  useTaskProcessingStore,
  isTaskTerminal,
} from '../../stores/taskProcessing.store';
import type { ParsedTaskStatusResponse } from '../../lib/schemas/task.schema';

const STATUS_LABELS: Record<ProcessingTask['normalizedStatus'], string> = {
  queued: 'En cola',
  processing: 'Procesando',
  retrying: 'Reintentando',
  success: 'Completado',
  failure: 'Error',
  canceled: 'Cancelado',
};

interface TaskMonitorItemProps {
  task: ProcessingTask;
}

export const TaskMonitorItem: React.FC<TaskMonitorItemProps> = ({ task }) => {
  const mergeTaskStatusResponse = useTaskProcessingStore(
    (state) => state.mergeTaskStatusResponse,
  );

  const handleTaskSuccess = React.useCallback(
    (payload: ParsedTaskStatusResponse) => {
      mergeTaskStatusResponse(payload);
    },
    [mergeTaskStatusResponse],
  );

  const { data, normalizedStatus } = useTaskStatusQuery(task.id, {
    enabled: !isTaskTerminal(task),
    onSuccess: handleTaskSuccess,
  });

  const statusKey = normalizedStatus ?? task.normalizedStatus;
  const statusLabel = STATUS_LABELS[statusKey];

  const taskTypeLabel = `${task.type}.${task.subtype}`;
  const derivedResultId = data?.resultId ?? task.resultId ?? null;

  return (
    <li
      data-task-id={task.id}
      data-task-status={statusKey}
      aria-label={taskTypeLabel}
    >
      <span>{taskTypeLabel}</span>
      <span>{statusLabel}</span>
      {derivedResultId !== null ? <span>#{derivedResultId}</span> : null}
    </li>
  );
};

TaskMonitorItem.displayName = 'TaskMonitorItem';
